/**
 * Client-side mirror of the shared blackjack table state. `WorldScene` rebuilds a
 * plain snapshot from the synced schema (plus the owner-only seat assignment) and
 * publishes it here; the table overlay subscribes. The server is authoritative —
 * this is a read model for rendering.
 */
import type { CharacterAppearance } from "@ghostland/shared";

export interface HandView {
  cards: string;
  value: number;
  soft: boolean;
  status: string;
  bet: number;
}

export interface SeatView {
  index: number;
  occupied: boolean;
  name: string;
  appearance: CharacterAppearance;
  connected: boolean;
  spectating: boolean;
  pendingBet: number;
  hand: HandView;
  splitHand: HandView;
  hasSplit: boolean;
}

export interface TableView {
  phase: string;
  activeSeat: number;
  activeSplitHand: boolean;
  timerEndsAt: number;
  dealer: { cards: string; value: number; soft: boolean };
  seats: SeatView[];
  /** Seat index this client holds, or -1 when not seated (owner-only knowledge). */
  mySeat: number;
}

export const EMPTY_TABLE: TableView = {
  phase: "idle",
  activeSeat: -1,
  activeSplitHand: false,
  timerEndsAt: 0,
  dealer: { cards: "", value: 0, soft: false },
  seats: [],
  mySeat: -1,
};

type Listener = (table: TableView) => void;

class BlackjackStore {
  private table: TableView = EMPTY_TABLE;
  private readonly listeners = new Set<Listener>();

  get(): TableView {
    return this.table;
  }

  set(table: TableView): void {
    this.table = table;
    for (const fn of this.listeners) fn(this.table);
  }

  /** Subscribe; immediately invoked with the current value. Returns unsubscribe. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.table);
    return () => this.listeners.delete(fn);
  }
}

export const blackjackStore = new BlackjackStore();
